/**
 * Financial statements derived live from the ledger (ARCHITECTURE §3.1):
 * profit & loss and balance sheet. Nothing here is stored — statements are a
 * pure function of the journal, so they cannot drift from the source of truth.
 */
import type { Account, Currency, JournalEntry, Money } from "./types";
import { add, subtract, zero } from "./money";
import { buildLedger, type AccountBalance } from "./ledger";

const FX_GAIN_LOSS = "7800";
const CT_EXPENSE = "8000";
const RETAINED_EARNINGS = "3900";

export interface StatementLine {
  readonly code: string;
  readonly label: string;
  readonly amount: Money;
}

export interface IncomeStatement {
  readonly revenue: Money;
  readonly costOfSales: Money;
  readonly grossProfit: Money;
  readonly operatingExpenses: Money;
  readonly fxResult: Money; // signed: positive = gain
  readonly profitBeforeTax: Money;
  readonly corporateTax: Money; // booked provision
  readonly netProfit: Money;
  readonly revenueLines: readonly StatementLine[];
  readonly expenseLines: readonly StatementLine[];
  /** Component accounts that roll up into cost of sales (for the detailed view). */
  readonly costOfSalesLines: readonly StatementLine[];
  /** Component accounts that roll up into operating expenses (detailed view). */
  readonly operatingExpenseLines: readonly StatementLine[];
}

/**
 * Cost-of-sales accounts are the 5xxx range; operating expenses are everything
 * else in the expense class except the FX (7800) and corporate-tax (8000)
 * accounts, which are presented on their own lines. Classifying by code range
 * (not a single hard-coded account) lets a tenant add sub-accounts — e.g. a
 * "Claude tokens / subscriptions" 5xxx account — and have them roll up
 * automatically and appear in the detailed breakdown.
 */
function isCostOfSales(code: string): boolean {
  return code.startsWith("5");
}

export interface BalanceSheet {
  readonly assets: readonly StatementLine[];
  readonly totalAssets: Money;
  readonly liabilities: readonly StatementLine[];
  readonly equity: readonly StatementLine[];
  readonly currentEarnings: Money;
  readonly totalLiabilitiesEquity: Money;
  /** True when assets = liabilities + equity (always, from a tied ledger). */
  readonly balances: boolean;
}

function line(bal: AccountBalance, accounts: Map<string, Account>): StatementLine {
  const acct = accounts.get(bal.code);
  return { code: bal.code, label: acct?.name ?? bal.code, amount: bal.balance };
}

export function incomeStatement(
  entries: readonly JournalEntry[],
  accounts: readonly Account[],
  base: Currency,
  entityId?: string,
): IncomeStatement {
  const ledger = buildLedger(entries, accounts, base, entityId);
  const byCode = new Map(accounts.map((a) => [a.code, a]));

  let revenue = zero(base);
  let costOfSales = zero(base);
  let operatingExpenses = zero(base);
  let fxResult = zero(base);
  let corporateTax = zero(base);
  const revenueLines: StatementLine[] = [];
  const expenseLines: StatementLine[] = [];
  const costOfSalesLines: StatementLine[] = [];
  const operatingExpenseLines: StatementLine[] = [];

  for (const bal of [...ledger.values()].sort((a, b) => a.code.localeCompare(b.code))) {
    const acct = byCode.get(bal.code);
    if (!acct) continue;
    if (bal.code === FX_GAIN_LOSS) {
      fxResult = bal.balance; // income-type: positive balance = gain
      continue;
    }
    if (acct.type === "income") {
      revenue = add(revenue, bal.balance);
      revenueLines.push(line(bal, byCode));
    } else if (acct.type === "expense") {
      const l = line(bal, byCode);
      expenseLines.push(l);
      if (bal.code === CT_EXPENSE) {
        corporateTax = add(corporateTax, bal.balance);
      } else if (isCostOfSales(bal.code)) {
        costOfSales = add(costOfSales, bal.balance);
        costOfSalesLines.push(l);
      } else {
        operatingExpenses = add(operatingExpenses, bal.balance);
        operatingExpenseLines.push(l);
      }
    }
  }

  const grossProfit = subtract(revenue, costOfSales);
  const profitBeforeTax = add(subtract(grossProfit, operatingExpenses), fxResult);
  const netProfit = subtract(profitBeforeTax, corporateTax);

  return {
    revenue,
    costOfSales,
    grossProfit,
    operatingExpenses,
    fxResult,
    profitBeforeTax,
    corporateTax,
    netProfit,
    revenueLines,
    expenseLines,
    costOfSalesLines,
    operatingExpenseLines,
  };
}

export function balanceSheet(
  entries: readonly JournalEntry[],
  accounts: readonly Account[],
  base: Currency,
  entityId?: string,
): BalanceSheet {
  const ledger = buildLedger(entries, accounts, base, entityId);
  const byCode = new Map(accounts.map((a) => [a.code, a]));

  const assets: StatementLine[] = [];
  const liabilities: StatementLine[] = [];
  const equity: StatementLine[] = [];
  let totalAssets = zero(base);
  let totalLiab = zero(base);
  let totalEquity = zero(base);
  // Current-year earnings = income − expense, added to equity so A = L + E.
  let currentEarnings = zero(base);

  for (const bal of [...ledger.values()].sort((a, b) => a.code.localeCompare(b.code))) {
    const acct = byCode.get(bal.code);
    if (!acct) continue;
    const l = line(bal, byCode);
    switch (acct.type) {
      case "asset":
        assets.push(l);
        totalAssets = add(totalAssets, bal.balance);
        break;
      case "liability":
        liabilities.push(l);
        totalLiab = add(totalLiab, bal.balance);
        break;
      case "equity":
        equity.push(l);
        totalEquity = add(totalEquity, bal.balance);
        break;
      case "income":
        currentEarnings = add(currentEarnings, bal.balance);
        break;
      case "expense":
        currentEarnings = subtract(currentEarnings, bal.balance);
        break;
    }
  }

  // Present retained earnings + current-year earnings as one "Retained earnings"
  // figure when a retained-earnings account exists; otherwise show it separately.
  const hasRetained = equity.some((e) => e.code === RETAINED_EARNINGS);
  const equityOut = hasRetained
    ? equity.map((e) =>
        e.code === RETAINED_EARNINGS
          ? { ...e, amount: add(e.amount, currentEarnings) }
          : e,
      )
    : equity;

  const totalLiabilitiesEquity = add(add(totalLiab, totalEquity), currentEarnings);
  return {
    assets,
    totalAssets,
    liabilities,
    equity: equityOut,
    currentEarnings,
    totalLiabilitiesEquity,
    balances: totalAssets.amount === totalLiabilitiesEquity.amount,
  };
}
